import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Banner } from '@els/els-react--banner';
import { TextInput } from '@els/els-react--text-input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { Checkbox } from '@els/els-react--checkbox';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import InnerHTML from '@Components/InnerHTML';
import { allowedTagsDefault } from '@Services/HtmlSanitizeService';
import { paths } from '../../../AppRoutes/constants';
import LoginFormHeader from './LoginFormHeader';
import LoginBlockedMessage from './LoginBlockedMessage';

import './LoginForm.scss';

let timerConst;

function LoginForm({
  isAnonymousUser,
  cancel,
  ipAddress,
  loginFail,
  loginFailMessage,
  isLoading,
  isFormDisabled,
  openForgotPasswordDialog,
  submit,
  hasInstitutionLogin,
  hasRegistration,
  loginBlockedTime,
  data,
  setData,
  notifications,
  onInstitutionLogin,
  onRegister,
}) {
  const spinnerId = 'loginForm';
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const [timer, setTimer] = useState(null);
  const history = useHistory();

  useEffect(() => {
    if (loginFail || loginFailMessage) {
      setIsVisible(true);
    }
  }, [loginFail, loginFailMessage]);

  const clearTimer = () => {
    clearTimeout(timerConst);
  };

  useEffect(() => {
    if (timerConst) {
      clearTimer();
    }

    if (!loginBlockedTime && loginBlockedTime !== 0) {
      return;
    }

    const roundedDifference = Math.round(loginBlockedTime);
    if (roundedDifference <= 300 && roundedDifference >= 0) {
      const seconds = 300 - roundedDifference;
      setTimer(seconds);

      setTimeout(() => setTimer(null), seconds * 1000);
    }

    // eslint-disable-next-line consistent-return
    return () => clearTimer();
  }, [loginBlockedTime]);

  const resetPassword = (e) => {
    e.preventDefault();
    openForgotPasswordDialog();
  };

  const submitHandler = () => {
    if (isFormDisabled) {
      return;
    }
    setIsSubmitted(true);
    submit(data);
  };

  /*
   * loginFailMessage - the error message which is returned on login page itself, e.g. incorrect credentials
   * loginFail - the error message which is passed from other components, e.g. from AuthInterceptor
   */
  const showErrorMessage = loginFail || loginFailMessage;
  const changeUserName = (value) => {
    setData({
      username: value,
    });
  };

  const changePassword = (value) => {
    setData({
      password: value,
    });
  };

  const changeRememberMe = (e) => {
    setData({
      remember_me: e?.target?.checked,
    });
  };

  return (
    <div className="login-form login-page-form-wrapper">
      <LoginFormHeader
        cancel={cancel}
        isAnonymousUser={isAnonymousUser}
        ipAddress={ipAddress}
        actionMessage="Sign in"
        infoMessage="Enter your email or username and password to continue.">
        <LoginBlockedMessage timer={timer} />
      </LoginFormHeader>
      {notifications &&
        isBannerVisible &&
        (notifications.CUSTOM_LEFT?.length > 0 || notifications.CUSTOM_RIGHT?.length > 0 || notifications.INSTITUTIONAL_GREETING) && (
          <Banner className="u-els-margin-top" size="small" isVisible onClose={() => setIsBannerVisible(false)} hideCloseLabel>
            {notifications.CUSTOM_LEFT?.map((notification) => (
              <InnerHTML
                key={`notification-left-${notification}`}
                tag="div"
                html={notification}
                mode="escape"
                allowedTags={allowedTagsDefault.concat('a')}
                allowedAttributes={['href', 'target']}
              />
            ))}
            {notifications.CUSTOM_RIGHT?.map((notification) => (
              <InnerHTML
                key={`notification-right-${notification}`}
                tag="div"
                html={notification}
                mode="escape"
                allowedTags={allowedTagsDefault.concat('a')}
                allowedAttributes={['href', 'target']}
              />
            ))}
            {notifications.INSTITUTIONAL_GREETING && (
              <InnerHTML
                tag="div"
                html={notifications.INSTITUTIONAL_GREETING}
                mode="escape"
                allowedTags={allowedTagsDefault.concat('a')}
                allowedAttributes={['href', 'target']}
              />
            )}
          </Banner>
        )}
      <form name="loginForm">
        <div className="login-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div aria-live="polite" aria-atomic="true">
            {showErrorMessage && (
              <Banner
                className="u-els-margin-top"
                id="login-error-message"
                type="warn"
                size="small"
                isVisible={isVisible}
                onClose={() => setIsVisible(false)}
                hideCloseLabel>
                {(isSubmitted ? loginFailMessage : loginFail) || showErrorMessage}
              </Banner>
            )}
          </div>
          <div className="els-text-input">
            <TextInput
              id="id-login-username"
              name="Username"
              autoComplete="username"
              className="u-els-margin-top"
              disabled={isFormDisabled}
              data-e2e="login-username-input"
              placeholder="Enter username or email"
              required
              label="Email address or username"
              value={data.username}
              onChange={(e) => changeUserName(e?.target?.value)}
            />
          </div>
          <div className="els-text-input">
            <TextInput
              id="id-login-password"
              name="Password"
              label="Password"
              autoComplete="current-password"
              disabled={isFormDisabled}
              className="u-els-margin-top"
              data-e2e="login-password-input"
              placeholder="Enter your password"
              required
              value={data.password}
              onChange={(e) => changePassword(e?.target?.value)}
              type="password"
            />
          </div>
          <div className="u-els-margin-top u-els-margin-bottom">
            <LinkElement
              inline
              isExternal
              noExternalIcon
              iconEmphasis
              href="#"
              data-e2e="login-forgot-password-link"
              onClick={resetPassword}>
              Forgot your password?
            </LinkElement>
          </div>

          <div className="els-text-input els-checkbox-input">
            <div className="login-form__content_remember">
              <Checkbox
                onChange={changeRememberMe}
                checked={data.remember_me}
                disabled={isFormDisabled}
                data-e2e="remember-me-checkbox"
                aria-checked={data.remember_me}
                id="login_remember"
                label="Stay signed in (not recommended for shared devices)"
                // ariaControlsGuidance="Stay signed in on this computer. Do not use on a shared computer!"
              />
            </div>
          </div>
          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button">
              <Button
                type="primary"
                expanded
                name="Login"
                disabled={isLoading || timer > 0}
                onClick={submitHandler}
                className="e2e-login-sign-in"
                data-e2e="login-sign-in-button">
                Sign in
              </Button>
            </div>
          </div>
        </div>
      </form>
      {hasInstitutionLogin && (
        <>
          <div className="login-form__message login-divider u-els-margin-top-1o2">
            <div className="separator-line" />
            <div className="separator-content u-els-margin-left-1o2">Or continue with:</div>
            <div className="separator-line u-els-margin-left-1o2" />
          </div>
          <div className="login-form__message">
            <ButtonWithIcon
              type="tertiary"
              sprite="Institution"
              name="Institution sign in"
              onClick={() => {
                if (onInstitutionLogin) {
                  onInstitutionLogin();
                } else {
                  history.push('/institution');
                }
              }}
              expanded
              className="u-els-margin-top-1x1o2 e2e-login-sign-in"
              data-e2e="login-institution-sign-in-button">
              Institution sign in
            </ButtonWithIcon>
          </div>
        </>
      )}

      {!hasRegistration && (
        <span
          className="login-form__message signup-trouble u-els-margin-top-1x1o2"
          data-click-delegation
          data-tracking="true"
          data-tracking-type="productfeatureused"
          data-tracking-action="not registered">
          <span>Don’t have an account?</span>
          <LinkElement
            className="u-els-margin-left-1o4"
            data-e2e="login-register-link"
            isExternal
            noExternalIcon
            iconEmphasis
            inline
            onClick={onRegister || (() => history.push(paths.REGISTER))}>
            Register
          </LinkElement>
        </span>
      )}

      <div className="login-form__message u-els-margin-top-1x1o2">
        <LinkElement
          isExternal
          data-e2e="login-privacy-policy-button"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/privacypolicy?dgcid=RN_AGCM_Sourced_300005028"
          target="_blank"
          rel="noreferrer">
          Privacy Policy
        </LinkElement>
        <LinkElement
          isExternal
          data-e2e="login-terms-and-conditions-button"
          className="u-els-margin-left"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
          target="_blank"
          rel="noreferrer">
          Terms and Conditions
        </LinkElement>
      </div>
    </div>
  );
}

LoginForm.propTypes = {
  isAnonymousUser: PropTypes.bool,
  cancel: PropTypes.func.isRequired,
  ipAddress: PropTypes.string,
  loginFail: PropTypes.string,
  loginFailMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  isFormDisabled: PropTypes.bool,
  openForgotPasswordDialog: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  hasInstitutionLogin: PropTypes.bool,
  onInstitutionLogin: PropTypes.func,
  hasRegistration: PropTypes.bool,
  loginBlockedTime: PropTypes.number,
  data: PropTypes.object,
  setData: PropTypes.func,
  notifications: PropTypes.object,
  onRegister: PropTypes.func,
};

export default LoginForm;
